// Player movement rules. Client prediction and the server run this exact code on the exact same inputs,
// using whole numbers only, so they always land on the same answer.
import { SUB } from './constants.js';
import type { ZoneMap } from './map.js';
import { AIRSHIP, mountByKind } from './mounts.js';

/** One tick of input, packed into bits. Edge actions (dash, attack, mount) are set only on the tick the key went
 *  down. Jump is held (a press jumps; holding it while flying climbs); the rules notice the press themselves. */
export const IN = { up: 1, down: 2, left: 4, right: 8, run: 16, dash: 32, attack: 64, jump: 128, mount: 256 } as const;
export const IN_MASK = 511;

export enum Dir { Down = 0, Right = 1, Up = 2, Left = 3 }
export const DIR_NAMES = ['down', 'right', 'up', 'left'] as const;

export enum Act { None = 0, Dash = 1, Attack = 2 }

/** Speeds in sub-pixels per tick (60 ticks a second, 64 sub-pixels a pixel). */
export const SPEED = {
  walk: 70,          // ~66 px/s, as in the single-player town
  run: 120,          // ~112 px/s
  dash: 267,         // ~250 px/s
  mount: 165,        // ~155 px/s
  gallop: 205,       // mounted and running
  fly: 190,
  ship: 300,         // an airship
} as const;
/** How high an airship sails (sub-pixels): over every mountain. */
export const AIRSHIP_Z = 176 * 64;
/** Height, in sub-pixels per tick. A jump peaks about 20 px up (28 on a mount) and lasts half a second. */
export const JUMP = { v: 170, mountV: 200, gravity: 11, flyUp: 60, flyDown: 70, flyMax: 112 * 64 } as const;
export const MOUNT_COOLDOWN_TICKS = 45;
export const DASH_TICKS = 10;           // ~170 ms
export const DASH_COOLDOWN_TICKS = 39;  // ~650 ms
export const ATTACK_TICKS = 26;         // the attack animation (1040 ms at 0.42 speed)
/** Tick at which each attack frame starts; frame 1 is the strike. */
export const ATTACK_FRAME_TICKS = [0, 6, 9, 13, 17];

const DIAG = 181; // 181/256 ≈ 1/√2

export interface MoveState {
  x: number; y: number;         // sub-pixels, feet position
  dir: Dir;
  act: Act; actT: number;       // current action and ticks left
  dvx: number; dvy: number;     // dash velocity (sub-pixels per tick)
  dashCd: number;               // ticks until the next dash
  moving: boolean; running: boolean;
  z: number; vz: number;        // height above the ground and how fast it's changing (sub-pixels)
  mount: number;                // 0 on foot, else the kind you're riding (shared/mounts.ts; AIRSHIP for an airship)
  flying: boolean;
  jumpHeld: boolean;            // was jump down last tick (so a press is noticed once)
  mountCd: number;
  steed: number;                // which mount you call when you press the mount key (the server sets it)
  seat: number;                 // riding with someone: their entity id (they do the moving), else 0
}

export function newMoveState(x: number, y: number): MoveState {
  return { x, y, dir: Dir.Up, act: Act.None, actT: 0, dvx: 0, dvy: 0, dashCd: 0, moving: false, running: false, z: 0, vz: 0, mount: 0, flying: false, jumpHeld: false, mountCd: 0, steed: 1, seat: 0 };
}

export const cloneMove = (s: MoveState): MoveState => ({ ...s });

export function sameMove(a: MoveState, b: MoveState): boolean {
  return a.x === b.x && a.y === b.y && a.dir === b.dir && a.act === b.act && a.actT === b.actT && a.dvx === b.dvx && a.dvy === b.dvy
    && a.dashCd === b.dashCd && a.moving === b.moving && a.running === b.running
    && a.z === b.z && a.vz === b.vz && a.mount === b.mount && a.flying === b.flying && a.jumpHeld === b.jumpHeld && a.mountCd === b.mountCd
    && a.steed === b.steed && a.seat === b.seat;
}

function dirOf(dx: number, dy: number): Dir {
  if (Math.abs(dx) > Math.abs(dy)) return dx > 0 ? Dir.Right : Dir.Left;
  return dy > 0 ? Dir.Down : Dir.Up;
}

/** Move one axis at a time; if the full step is blocked, take the largest half-step that fits so walls feel snug. */
function tryAxis(s: MoveState, map: ZoneMap, mx: number, my: number): boolean {
  let moved = false;
  for (const [ax, ay] of [[mx, 0], [0, my]] as const) {
    if (!ax && !ay) continue;
    let k = 1;
    for (let i = 0; i < 6; i++) {
      const nx = s.x + Math.trunc(ax / k), ny = s.y + Math.trunc(ay / k);
      if ((nx !== s.x || ny !== s.y) && map.fitsSub(nx, ny, s.z, s.flying)) { s.x = nx; s.y = ny; moved = true; break; }
      k *= 2;
    }
  }
  return moved;
}

/** Advance one player by one tick of input. Mutates and returns the state. */
export function stepMove(s: MoveState, input: number, map: ZoneMap): MoveState {
  if (s.z === undefined) { s.z = 0; s.vz = 0; s.mount = 0; s.flying = false; s.jumpHeld = false; s.mountCd = 0; }   // states saved before heights
  if (s.steed === undefined) { s.steed = 1; s.seat = 0; }                                                          // ... and before mounts
  if (s.seat) { s.moving = false; return s; }                     // riding with someone: they steer (the server moves you along)
  if (s.mount === AIRSHIP) return stepShip(s, input, map);
  const jumpPress = (input & IN.jump) !== 0 && !s.jumpHeld;
  s.jumpHeld = (input & IN.jump) !== 0;
  if (s.mountCd > 0) s.mountCd--;
  // a zone without mounts (town, rooms): off you hop
  if (s.mount && !map.mounts) { s.mount = 0; s.flying = false; }
  if (s.flying && !map.flying) s.flying = false;
  // mount or dismount: only standing on something, never mid-swing
  if ((input & IN.mount) && s.mountCd === 0 && s.act === Act.None) {
    const f = map.floorSub(s.x, s.y);
    if (s.mount && !s.flying && s.z === f.h) { s.mount = 0; s.mountCd = MOUNT_COOLDOWN_TICKS; }
    else if (!s.mount && map.mounts && s.z === f.h) { s.mount = s.steed || 1; s.mountCd = MOUNT_COOLDOWN_TICKS; }
  }
  if (s.mount) {   // riding: no dash or attack; jump, and take off where flying is allowed
    input &= ~(IN.attack | (s.flying ? 0 : IN.dash));
    ground(s, input, map, jumpPress);
    if (s.act === Act.Dash) s.act = Act.None;
    let dx = 0, dy = 0;
    if (input & IN.left) dx -= 1; if (input & IN.right) dx += 1; if (input & IN.up) dy -= 1; if (input & IN.down) dy += 1;
    s.running = (input & IN.run) !== 0;
    if (dx || dy) {
      const md = mountByKind(s.mount);
      let sp = s.flying ? (SPEED.fly * md.fly / 100) | 0 : (((s.running ? SPEED.gallop : SPEED.mount) * md.speed) / 100) | 0;
      if (dx && dy) sp = (sp * DIAG) >> 8;
      s.dir = dirOf(dx, dy);
      s.moving = tryAxis(s, map, dx * sp, dy * sp);
    } else s.moving = false;
    vertical(s, input, map);
    return s;
  }
  stepFoot(s, input, map, jumpPress);
  vertical(s, input, map);
  return s;
}

/** An airship: high above everything, fast; press the mount key over open ground to land (everyone gets off). */
function stepShip(s: MoveState, input: number, map: ZoneMap): MoveState {
  if (s.mountCd > 0) s.mountCd--;
  s.jumpHeld = (input & IN.jump) !== 0; s.act = Act.None; s.running = false; s.vz = 0;
  if (!map.airship) { s.mount = 0; s.flying = false; s.z = map.floorSub(s.x, s.y).h; return s; }
  s.flying = true; s.z = AIRSHIP_Z;
  if ((input & IN.mount) && s.mountCd === 0) {
    const f = map.floorSub(s.x, s.y);
    if (f.landable && map.fitsSub(s.x, s.y, f.h, false)) { s.mount = 0; s.flying = false; s.z = f.h; s.mountCd = MOUNT_COOLDOWN_TICKS; s.moving = false; return s; }
  }
  let dx = 0, dy = 0;
  if (input & IN.left) dx -= 1; if (input & IN.right) dx += 1; if (input & IN.up) dy -= 1; if (input & IN.down) dy += 1;
  if (dx || dy) {
    let sp: number = SPEED.ship;
    if (dx && dy) sp = (sp * DIAG) >> 8;
    s.dir = dirOf(dx, dy);
    s.moving = tryAxis(s, map, dx * sp, dy * sp);
  } else s.moving = false;
  return s;
}

/** Start a jump (or take off) when jump was just pressed. */
function ground(s: MoveState, _input: number, map: ZoneMap, jumpPress: boolean): void {
  if (!jumpPress || s.act === Act.Attack) return;
  const f = map.floorSub(s.x, s.y);
  if (s.z === f.h && s.vz === 0 && !s.flying) { s.vz = s.mount ? JUMP.mountV : JUMP.v; return; }
  if (s.mount && map.flying && !s.flying && mountByKind(s.mount).fly > 0) { s.flying = true; s.vz = 0; }   // a second press in the air: take off
}

/** Up and down: gravity and landing, or flight (hold jump to climb, hold dash to come down). */
function vertical(s: MoveState, input: number, map: ZoneMap): void {
  const f = map.floorSub(s.x, s.y);
  if (s.flying) {
    s.vz = (input & IN.jump) ? JUMP.flyUp : (input & IN.dash) ? -JUMP.flyDown : 0;
    s.z = Math.min(JUMP.flyMax, s.z + s.vz);
    if (s.z <= f.h) { s.z = f.h; if (s.vz < 0 && f.landable) { s.flying = false; s.vz = 0; } }
    return;
  }
  if (s.z > f.h || s.vz > 0) {
    s.z += s.vz; s.vz -= JUMP.gravity;
    if (s.z <= f.h) { s.z = f.h; s.vz = 0; }
  } else if (s.z < f.h && f.h - s.z <= STEP) { s.z = f.h; s.vz = 0; }   // a tiny step up
}
const STEP = 3 * 64;

function stepFoot(s: MoveState, input: number, map: ZoneMap, jumpPress: boolean): void {
  if (s.dashCd > 0) s.dashCd--;
  let dx = 0, dy = 0;
  if (input & IN.left) dx -= 1; if (input & IN.right) dx += 1; if (input & IN.up) dy -= 1; if (input & IN.down) dy += 1;

  if (s.act === Act.Dash) {
    tryAxis(s, map, s.dvx, s.dvy);
    s.moving = true;
    if (--s.actT <= 0) s.act = Act.None;
    return;
  }
  if (jumpPress) ground(s, input, map, true);
  if (s.act === Act.Attack) {
    s.moving = false;
    if (--s.actT <= 0) s.act = Act.None;
    return;
  }
  if ((input & IN.dash) && s.dashCd === 0) {
    let vx = dx, vy = dy;
    if (!vx && !vy) { vx = s.dir === Dir.Right ? 1 : s.dir === Dir.Left ? -1 : 0; vy = s.dir === Dir.Down ? 1 : s.dir === Dir.Up ? -1 : 0; }
    const sp = vx && vy ? (SPEED.dash * DIAG) >> 8 : SPEED.dash;
    s.act = Act.Dash; s.actT = DASH_TICKS; s.dashCd = DASH_COOLDOWN_TICKS; s.dvx = vx * sp; s.dvy = vy * sp; s.dir = dirOf(vx, vy);
    tryAxis(s, map, s.dvx, s.dvy); s.moving = true; s.actT--;
    return;
  }
  if ((input & IN.attack) && s.z === map.floorSub(s.x, s.y).h) {
    s.act = Act.Attack; s.actT = ATTACK_TICKS; s.moving = false;
    if (dx || dy) s.dir = dirOf(dx, dy);
    return;
  }
  s.running = (input & IN.run) !== 0;
  if (dx || dy) {
    let sp: number = s.running ? SPEED.run : SPEED.walk;
    if (map.data.walk) sp = (sp * map.data.walk / 100) | 0;
    if (dx && dy) sp = (sp * DIAG) >> 8;
    s.dir = dirOf(dx, dy);
    s.moving = tryAxis(s, map, dx * sp, dy * sp);
  } else s.moving = false;
}

/** What other players need to animate someone: a compact code. */
export enum Anim { Idle = 0, Walk = 1, Run = 2, Dash = 3, Attack = 4 }
export function animOf(s: MoveState): Anim {
  if (s.act === Act.Dash) return Anim.Dash;
  if (s.act === Act.Attack) return Anim.Attack;
  if (!s.moving) return Anim.Idle;
  return s.running ? Anim.Run : Anim.Walk;
}
